import React, { useState, useEffect, useMemo } from 'react';
import { Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Appointment, Case } from '../../types';
import { useDashboardDataContext } from '../../contexts/DashboardDataContext';
import { useCaseAccess } from '../../hooks/useCaseAccess';
import { useDashboardReport } from '../../contexts/DashboardReportContext';
import {
  buildTodayFocusModel,
  getActiveCases,
  getCalendarDatePath,
  TodayAttentionItem,
} from './dashboardUtils';
import TodayFocusSection from './TodayFocusSection';
import RecentActiveCases from './RecentActiveCases';
import QuickPanel from './QuickPanel';
import ResourcesWidget from './ResourcesWidget';
import WeeklyVerseWidget from './WeeklyVerseWidget';
import SessionReport from '../Cases/SessionReport';
import ConsentUploadDialog from '../Cases/ConsentUploadDialog';
import ScheduleAppointmentDialog from './ScheduleAppointmentDialog';
import CaseSelectionDialog from './CaseSelectionDialog';
import MeetingNoteFormDialog from '../MeetingNotes/MeetingNoteFormDialog';
import PrepareSessionDialog from './PrepareSessionDialog';
import { useSessionQuestions } from '../../contexts/SessionQuestionsContext';
import { useMonthlyReport } from '../../hooks/useMonthlyReport';
import {
  formatMonthKeyLabel,
  getDueReportMonthKey,
  getMonthlyReportReminderPhase,
} from '../MonthlyReport/monthlyReportUtils';
import { t } from '../../utils/translations';

type CaseSelectionPurpose = 'report' | 'note' | 'consent';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { registerOpenCaseReportModal } = useDashboardReport();
  const { offerAfterReport, openTablou } = useSessionQuestions();
  const {
    cases,
    ownCases,
    appointments,
    sessionReportCounts,
    upcomingAppointments,
    loading,
    error,
    incrementSessionReportCount,
    upsertCase,
    refetch,
  } = useDashboardDataContext();
  const access = useCaseAccess();

  const { ownReport, ownLoading } = useMonthlyReport();
  const dueMonthKey = getDueReportMonthKey();

  const [caseSelectionPurpose, setCaseSelectionPurpose] =
    useState<CaseSelectionPurpose | null>(null);
  const [sessionReportOpen, setSessionReportOpen] = useState(false);
  const [selectedCaseForReport, setSelectedCaseForReport] = useState<Case | null>(null);
  const [addNoteCase, setAddNoteCase] = useState<Case | null>(null);
  const [consentCase, setConsentCase] = useState<Case | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleCaseId, setScheduleCaseId] = useState<string | null>(null);
  const [scheduleSessionNumber, setScheduleSessionNumber] = useState<number | null>(null);
  const [prepareOpen, setPrepareOpen] = useState(false);
  const [prepareCase, setPrepareCase] = useState<Case | null>(null);
  const [prepareAppointment, setPrepareAppointment] = useState<Appointment | null>(null);
  const [prepareSessionNumber, setPrepareSessionNumber] = useState<number | null>(null);

  useEffect(() => {
    registerOpenCaseReportModal(() => setCaseSelectionPurpose('report'));
    return () => registerOpenCaseReportModal(null);
  }, [registerOpenCaseReportModal]);

  const handleCloseCaseSelection = () => setCaseSelectionPurpose(null);

  const handleSelectCase = (selectedCase: Case) => {
    const purpose = caseSelectionPurpose;
    setCaseSelectionPurpose(null);
    if (purpose === 'report') {
      setSelectedCaseForReport(selectedCase);
      setSessionReportOpen(true);
      return;
    }
    if (purpose === 'note') {
      setAddNoteCase(selectedCase);
      return;
    }
    if (purpose === 'consent') {
      setConsentCase(selectedCase);
    }
  };

  const handleAddReportForCase = (caseItem: Case) => {
    setSelectedCaseForReport(caseItem);
    setSessionReportOpen(true);
  };

  const handleCloseSessionReport = () => {
    setSessionReportOpen(false);
    setSelectedCaseForReport(null);
    setCaseSelectionPurpose(null);
  };

  const handleReportSaved = (result?: {
    caseId: string;
    meetingDate: Date;
    meetingFrequencyWeeks: Case['meetingFrequencyWeeks'];
    sessionNumber: number;
  }) => {
    const caseId = selectedCaseForReport?.id;
    const caseItem = selectedCaseForReport;
    setSessionReportOpen(false);
    setCaseSelectionPurpose(null);
    setSelectedCaseForReport(null);
    if (caseId && caseItem) {
      // Straight into the hand-off while the meeting is still fresh.
      offerAfterReport({ caseId, sessionNumber: result?.sessionNumber ?? null });
      incrementSessionReportCount(caseId);
      if (result?.caseId === caseId) {
        upsertCase({
          ...caseItem,
          lastMeetingDate: result.meetingDate,
          meetingFrequencyWeeks:
            result.meetingFrequencyWeeks ?? caseItem.meetingFrequencyWeeks ?? null,
          pendingClarifySession: result.sessionNumber,
          updatedAt: new Date(),
        });
      }
    }
    void refetch();
  };

  const handleCaseClosedFromReport = (updatedCase: Case) => {
    upsertCase(updatedCase);
    setSessionReportOpen(false);
    setCaseSelectionPurpose(null);
    setSelectedCaseForReport(null);
    void refetch();
  };

  const handleCancelAddForm = () => {
    setSessionReportOpen(false);
    setSelectedCaseForReport(null);
    setCaseSelectionPurpose(null);
  };

  const handleCloseAddNote = () => {
    setAddNoteCase(null);
    setCaseSelectionPurpose(null);
  };

  const handleNoteSaved = () => {
    setAddNoteCase(null);
    setCaseSelectionPurpose(null);
    void refetch();
  };

  const handleCloseConsent = () => {
    setConsentCase(null);
    setCaseSelectionPurpose(null);
  };

  const handleOpenSchedule = (caseItem?: Case, sessionNumber?: number) => {
    setScheduleCaseId(caseItem?.id ?? null);
    setScheduleSessionNumber(
      typeof sessionNumber === 'number' ? Math.max(0, Math.floor(sessionNumber)) : null
    );
    setScheduleOpen(true);
  };

  const handleCloseSchedule = () => {
    setScheduleOpen(false);
    setScheduleCaseId(null);
    setScheduleSessionNumber(null);
  };

  const handleAppointmentScheduled = (appointment: Appointment) => {
    setScheduleOpen(false);
    setScheduleCaseId(null);
    setScheduleSessionNumber(null);
    navigate(getCalendarDatePath(appointment.date));
  };

  const handleConsentUploaded = (caseItem: Case) => {
    upsertCase({
      ...caseItem,
      consentAttached: true,
      updatedAt: new Date(),
    });
    setConsentCase(null);
    setCaseSelectionPurpose(null);
    void refetch();
  };

  const openPrepare = (
    caseItem: Case,
    sessionNumber?: number | null,
    appointment?: Appointment | null
  ) => {
    setPrepareCase(caseItem);
    setPrepareSessionNumber(
      typeof sessionNumber === 'number' ? Math.max(0, Math.floor(sessionNumber)) : null
    );
    setPrepareAppointment(appointment ?? null);
    setPrepareOpen(true);
  };

  const handleClosePrepare = () => {
    setPrepareOpen(false);
  };

  const handlePrepareExited = () => {
    setPrepareCase(null);
    setPrepareAppointment(null);
    setPrepareSessionNumber(null);
  };

  const handleTodayPrepare = (item: TodayAttentionItem) => {
    if (!item.caseItem) return;
    openPrepare(item.caseItem, item.sessionNumber, item.appointment);
  };

  const handleTodayReport = (item: TodayAttentionItem) => {
    if (!item.caseItem) return;
    handleAddReportForCase(item.caseItem);
  };

  const handleTodaySchedule = (item: TodayAttentionItem) => {
    handleOpenSchedule(item.caseItem);
  };

  const handleTodayConsent = (item: TodayAttentionItem) => {
    if (!item.caseItem) return;
    setConsentCase(item.caseItem);
  };

  const handleTodayClarify = (item: TodayAttentionItem) => {
    if (!item.caseItem || item.sessionNumber == null) return;
    openTablou({ caseId: item.caseItem.id, sessionNumber: item.sessionNumber });
  };

  const handleTodayMonthlyReport = () => {
    navigate('/monthly-report');
  };

  const activeCases = getActiveCases(ownCases);
  // Notes are the one thing an apprentice may write, so that picker keeps the
  // shadowed cases the pairing still allows.
  const activeNoteCases = getActiveCases(
    cases.filter((caseItem) => access.canAddNote(caseItem.id))
  );
  const caseSelectionList =
    caseSelectionPurpose === 'consent'
      ? activeCases.filter((c) => c.consentAttached !== true)
      : caseSelectionPurpose === 'note'
        ? activeNoteCases
        : activeCases;

  const caseSelectionTitle =
    caseSelectionPurpose === 'note'
      ? t.sessionReports.selectCaseForNote
      : caseSelectionPurpose === 'consent'
        ? t.sessionReports.selectCaseForConsent
        : t.sessionReports.selectCaseForReport;

  const caseSelectionEmpty =
    caseSelectionPurpose === 'note'
      ? t.sessionReports.noActiveCasesForNote
      : caseSelectionPurpose === 'consent'
        ? t.sessionReports.noActiveCasesForConsent
        : t.sessionReports.noActiveCases;
  const todayFocus = useMemo(
    () =>
      buildTodayFocusModel(ownCases, appointments, sessionReportCounts, new Date(), {
        missing: !ownLoading && !ownReport,
        phase: getMonthlyReportReminderPhase(),
        monthKey: dueMonthKey,
        monthLabel: formatMonthKeyLabel(dueMonthKey),
      }),
    [ownCases, appointments, sessionReportCounts, ownLoading, ownReport, dueMonthKey]
  );

  if (error) {
    return (
      <Alert severity="error" className="rounded-xl">
        {error}
      </Alert>
    );
  }

  return (
    /**
     * From `lg` the widgets form the right column. Below it the rail is
     * `display: contents`, so its blocks join the page order directly:
     * focus, appointments + quick actions, cases, then the remaining widgets.
     * The rail spans both rows and is usually taller than the left column, so
     * the last row takes the slack instead of it being split between the rows.
     */
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_440px] lg:grid-rows-[auto_1fr] lg:items-start lg:gap-x-8 lg:gap-y-4 xl:grid-cols-[minmax(0,1fr)_460px] xl:gap-x-10">
      <div className="order-1 lg:col-start-1 lg:row-start-1">
        <TodayFocusSection
          focus={todayFocus}
          loading={loading || ownLoading}
          onPrepare={handleTodayPrepare}
          onReport={handleTodayReport}
          onSchedule={handleTodaySchedule}
          onConsent={handleTodayConsent}
          onClarify={handleTodayClarify}
          onAddNote={(item) => {
            if (item.caseItem) setAddNoteCase(item.caseItem);
          }}
          onMonthlyReport={handleTodayMonthlyReport}
        />
      </div>

      <div className="contents lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:flex lg:flex-col lg:gap-4">
        <div className="order-2">
          <QuickPanel
            appointments={upcomingAppointments}
            loading={loading}
            onViewCalendar={() => navigate('/calendar')}
            onRaportCaz={() => setCaseSelectionPurpose('report')}
            onAddNote={() => setCaseSelectionPurpose('note')}
            onSchedule={() => handleOpenSchedule()}
          />
        </div>
        <div className="order-4 flex flex-col gap-4">
          <ResourcesWidget onOpenResources={() => navigate('/resources')} />
          <WeeklyVerseWidget />
        </div>
      </div>

      <div className="order-3 lg:col-start-1 lg:row-start-2">
        <RecentActiveCases
          cases={activeCases}
          sessionReportCounts={sessionReportCounts}
          upcomingAppointments={upcomingAppointments}
          loading={loading}
          onAddReport={handleAddReportForCase}
          onAddNote={setAddNoteCase}
          onSchedule={handleOpenSchedule}
          onPrepare={(caseItem, session, appointment) =>
            openPrepare(caseItem, session, appointment)
          }
        />
      </div>

      <CaseSelectionDialog
        open={caseSelectionPurpose !== null}
        title={caseSelectionTitle}
        cases={caseSelectionList}
        loading={loading}
        emptyMessage={caseSelectionEmpty}
        onSelect={handleSelectCase}
        onClose={handleCloseCaseSelection}
      />

      <SessionReport
        open={sessionReportOpen}
        onClose={handleCloseSessionReport}
        caseId={selectedCaseForReport?.id || ''}
        caseTitle={selectedCaseForReport?.title || ''}
        caseItem={selectedCaseForReport}
        onReportAdded={handleReportSaved}
        onCaseClosed={handleCaseClosedFromReport}
        onCancelAddForm={handleCancelAddForm}
        hideAddButton={false}
        caseStatus={selectedCaseForReport?.status}
        autoOpenAddForm
      />

      <MeetingNoteFormDialog
        open={addNoteCase !== null}
        caseItem={addNoteCase}
        reportCount={
          addNoteCase ? sessionReportCounts[addNoteCase.id] ?? 0 : 0
        }
        onClose={handleCloseAddNote}
        onSaved={handleNoteSaved}
      />

      <ConsentUploadDialog
        open={consentCase !== null}
        caseItem={consentCase}
        onClose={handleCloseConsent}
        onUploaded={handleConsentUploaded}
      />

      <ScheduleAppointmentDialog
        open={scheduleOpen}
        preSelectedCaseId={scheduleCaseId}
        preSelectedSessionNumber={scheduleSessionNumber}
        onClose={handleCloseSchedule}
        onScheduled={handleAppointmentScheduled}
      />

      <PrepareSessionDialog
        open={prepareOpen}
        caseItem={prepareCase}
        appointment={prepareAppointment}
        sessionNumber={prepareSessionNumber}
        reportCount={prepareCase ? sessionReportCounts[prepareCase.id] ?? 0 : 0}
        onClose={handleClosePrepare}
        onExited={handlePrepareExited}
        onAddReport={handleAddReportForCase}
      />
    </div>
  );
};

export default Dashboard;
